/**
 * Manejador de input del terminal CRT.
 *
 * Listeners:
 *  - `keydown` global: en vistas con prompt, acumula caracteres en
 *    `state.inputBuffer`. Enter submitea, Esc vuelve, Backspace borra.
 *    En vista MENU, 1-6 navegan.
 *  - Click en `.opt[data-key]` del menú: equivalente a teclear ese
 *    número.
 *
 * Cuando entra a `register_turnstile`, monta el widget Turnstile
 * (carga el script si hace falta) y guarda el token cuando el usuario
 * resuelve el reto. Cuando sale, lo elimina.
 *
 * Cuando ocurre una transición que necesita pintar el render (cambio
 * de vista, error, etc.), llama al `onChange` que pasamos.
 */

import * as api from './api.js';
import * as audio from './audio.js';
import { saveStoredProfile, saveStoredTimeOfDay } from './crt-controls.js';
import { triggerDegauss } from './degauss.js';
import {
  isPasswordView,
  processInput,
  selectAboutOption,
  selectConfigOption,
  selectMenuOption,
  selectProfileOption,
  selectTimeOfDayOption,
} from './flows.js';
import { getLegalDocument } from '../legal/content.js';
import { formatLegalDocForCrt } from '../legal/crt-format.js';
import { detectActiveSubstitutions } from './font-substitutions.js';
import { messageFor } from './messages.js';
import { applyProfile, getProfile } from './monitor-profiles.js';
import { TRANSITION_FLAGS, triggerShutdown } from '../scene/transitions.js';
import { loadTurnstile } from './turnstile-loader.js';
import {
  clearInput,
  clearLog,
  pushLine,
  type AppState,
  type ViewId,
} from './state.js';

interface Refs {
  /** Contenedor `<div class="terminal">`. */
  terminal: HTMLElement;
}

interface Options {
  state: AppState;
  refs: Refs;
  /** Llamado tras cada cambio relevante para forzar re-render. */
  onChange: () => void;
}

const NAVIGATION_KEYS = new Set([
  'Tab',
  'F5',
  'F12',
  'Meta',
]);

let widgetId: string | null = null;

export function attachInputHandler(opts: Options): void {
  const { state, refs, onChange } = opts;

  // Si arrancamos directamente en una vista distinta del menú, hay que
  // ejecutar la entrada de esa vista (montar Turnstile, etc.).
  void onEnterView(state.view, opts);

  // El `[ESC] SALIR` del case es un <a href="/"> nativo. Lo interceptamos
  // para disparar la transición CRT-off (Fase 12b-1) antes de navegar.
  // Click con modificador o middle-click se respetan (atajos de
  // "abrir en nueva pestaña").
  const backLink = document.querySelector<HTMLAnchorElement>('.back-link');
  backLink?.addEventListener('click', (ev) => {
    if (ev.button !== 0) return;
    if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    audio.playButtonClick();
    triggerShutdown('/', TRANSITION_FLAGS.WAS_IN_CRT);
  });

  /* ─── keyboard ─────────────────────────────────────────────────── */
  window.addEventListener('keydown', async (ev) => {
    // Filtramos modificadores que indican atajos (Ctrl, Cmd) pero NO Alt/Option:
    // en teclados internacionales (Mac ES, Linux con AltGr, etc.) muchos
    // caracteres imprimibles necesarios para escribir un email — @ € ñ | \ #
    // — se producen con Alt+tecla. Bloquearlos hace imposible registrarse.
    if (ev.ctrlKey || ev.metaKey) return;
    if (NAVIGATION_KEYS.has(ev.key)) return;

    // Esc: desde una sub-vista vuelve al menú; desde el menú sale del
    // CRT (mismo efecto que la opción 6 SALIR — el footer del CRT lo
    // anuncia con `[ESC] SALIR`). La salida dispara la animación CRT-off
    // (Fase 12b-1).
    if (ev.key === 'Escape') {
      ev.preventDefault();
      if (state.view === 'about_doc') {
        // Desde un documento legal, ESC vuelve al submenú ACERCA DE.
        await transitionTo('about', opts, { resetLog: true });
      } else if (state.view !== 'menu') {
        await transitionTo('menu', opts, { resetLog: true });
      } else {
        audio.playButtonClick();
        triggerShutdown('/', TRANSITION_FLAGS.WAS_IN_CRT);
      }
      return;
    }

    if (state.view === 'menu') {
      if (ev.key >= '1' && ev.key <= '9') {
        ev.preventDefault();
        audio.playKeyClick();
        await applyEffect(selectMenuOption(ev.key, state), opts);
      }
      return;
    }

    if (state.view === 'config') {
      if (ev.key >= '1' && ev.key <= '9') {
        ev.preventDefault();
        audio.playKeyClick();
        await applyEffect(selectConfigOption(ev.key), opts);
      }
      return;
    }

    if (state.view === 'config_screen_profile') {
      if (ev.key >= '1' && ev.key <= '9') {
        ev.preventDefault();
        audio.playKeyClick();
        await applyEffect(selectProfileOption(ev.key), opts);
      }
      return;
    }

    if (state.view === 'config_time_of_day') {
      if (ev.key >= '1' && ev.key <= '9') {
        ev.preventDefault();
        audio.playKeyClick();
        await applyEffect(selectTimeOfDayOption(ev.key, state.currentTimeOfDay), opts);
      }
      return;
    }

    if (state.view === 'about') {
      if (ev.key >= '1' && ev.key <= '9') {
        ev.preventDefault();
        audio.playKeyClick();
        await applyEffect(selectAboutOption(ev.key), opts);
      }
      return;
    }

    // Vistas "ocupadas" o de mensaje: ignorar entrada.
    if (state.inputDisabled) return;
    if (state.view === 'about_doc' || state.view === 'register_done') {
      // Solo Esc (ya capturado arriba) o el back-link.
      return;
    }

    // Vistas con prompt activo:
    if (ev.key === 'Enter') {
      ev.preventDefault();
      audio.playKeyClick();
      await onEnter(opts);
      return;
    }
    if (ev.key === 'Backspace') {
      ev.preventDefault();
      if (state.inputBuffer.length > 0) {
        state.inputBuffer = state.inputBuffer.slice(0, -1);
        audio.playKeyClick();
        onChange();
      }
      return;
    }
    if (ev.key.length === 1) {
      // Carácter imprimible normal.
      ev.preventDefault();
      state.inputBuffer += ev.key;
      audio.playKeyClick();
      onChange();
    }
  });

  /* ─── click en opciones (menú principal y sub-menús) ───────────── */
  refs.terminal.addEventListener('click', async (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const opt = target.closest<HTMLElement>('.opt');
    if (!opt || opt.dataset.disabled === 'true') return;
    const key = opt.dataset.key;
    if (!key) return;
    audio.playKeyClick();
    // Despachamos según la vista activa para que un click en una opción
    // del sub-menú CONFIG no termine procesado como opción del menú
    // principal.
    if (state.view === 'menu') {
      await applyEffect(selectMenuOption(key, state), opts);
    } else if (state.view === 'config') {
      await applyEffect(selectConfigOption(key), opts);
    } else if (state.view === 'config_screen_profile') {
      await applyEffect(selectProfileOption(key), opts);
    } else if (state.view === 'config_time_of_day') {
      await applyEffect(selectTimeOfDayOption(key, state.currentTimeOfDay), opts);
    } else if (state.view === 'about') {
      await applyEffect(selectAboutOption(key), opts);
    }
  });
}

/* ─── Lógica de transición ────────────────────────────────────────────── */

async function onEnter(opts: Options): Promise<void> {
  const { state, onChange } = opts;
  const submitted = state.inputBuffer;

  // Volcar el input al log antes de procesarlo.
  if (state.view !== 'register_turnstile') {
    const prompt = visiblePrompt(state.view);
    const visible = state.inputIsPassword
      ? '*'.repeat(submitted.length)
      : submitted;
    pushLine(state, `${prompt} ${visible}`, 'normal');
  }
  state.inputBuffer = '';
  state.inputDisabled = true;
  // Cambia a vista busy si la submission llamará a la API.
  if (state.view === 'login_password') state.view = 'login_busy';
  else if (state.view === 'register_turnstile') state.view = 'register_busy';
  onChange();

  const effect = await processInput(
    { ...opts.state, view: previousActionableView(state.view) },
    submitted,
  );
  await applyEffect(effect, opts);
}

async function applyEffect(effect: Awaited<ReturnType<typeof processInput>>, opts: Options): Promise<void> {
  const { state, onChange } = opts;

  switch (effect.kind) {
    case 'goto':
      await transitionTo(effect.view, opts);
      break;
    case 'error':
      pushLine(state, effect.text, 'error');
      audio.playLoginFail();
      if (effect.gotoView) {
        await transitionTo(effect.gotoView, opts, { keepLog: true });
      } else {
        // Mismo prompt, listo para reintentar.
        clearInput(state);
        state.inputIsPassword = isPasswordView(state.view);
        onChange();
      }
      break;
    case 'redirect':
      if (effect.greeting) {
        pushLine(state, effect.greeting, 'msg');
      }
      audio.playLoginOk();
      onChange();
      window.setTimeout(() => {
        window.location.replace(effect.url);
      }, 600);
      break;
    case 'message':
      pushLine(state, effect.text, 'info');
      await transitionTo(effect.gotoView, opts, { keepLog: true });
      break;
    case 'logout':
      await doLogout(opts);
      break;
    case 'apply_profile': {
      // Aplicar perfil: cambiar DOM + estado + persistencia + re-render
      // (para que el (X) del listado se mueva). El usuario puede probar
      // varios consecutivos sin salir del selector.
      state.currentProfile = effect.profileId;
      const profile = getProfile(effect.profileId);
      applyProfile(effect.profileId);
      saveStoredProfile(effect.profileId);
      // Visual: degauss el monitor al cambiar de fósforo.
      const screen = opts.refs.terminal.parentElement;
      if (screen) triggerDegauss(screen);
      onChange();
      // Re-detectar sustituciones de tildes: la nueva fuente puede tener
      // un mapeo distinto al previo (p.ej. Amstrad CPC464 mantiene
      // tildes, Perfect DOS las sustituye en mayúsculas).
      // Re-render tras la detección para que el header y el log se
      // pinten con las sustituciones correctas.
      await detectActiveSubstitutions(opts.refs.terminal);
      onChange();
      // Cambiar el ambiente sonoro según el perfil. Si el perfil tiene
      // ambientSample, reemplaza al sintético por el sample loop; si
      // no, restaura el sintético clásico. setAmbient memoriza la
      // elección aunque el usuario esté mute o el ctx aún no se haya
      // creado por falta de gesto.
      void audio.setAmbient(profile.ambientSample ?? null);
      break;
    }
    case 'apply_time_of_day': {
      // Persistir la preferencia + actualizar state para que el (X) del
      // listado se mueva. No tocamos el CRT — el efecto vive en la sala,
      // se aplicará al navegar de vuelta a `/`.
      state.currentTimeOfDay = effect.pref;
      saveStoredTimeOfDay(effect.pref);
      onChange();
      break;
    }
    case 'show_about_doc':
      // Guardar qué documento legal se ha pedido y entrar a `about_doc`,
      // que lo volcará en el terminal. resetLog para leerlo limpio, sin
      // el submenú anterior encima.
      state.aboutDocId = effect.docId;
      await transitionTo('about_doc', opts, { resetLog: true });
      break;
    case 'noop':
      break;
  }
}

async function doLogout(opts: Options): Promise<void> {
  const { state, onChange } = opts;
  await api.logout();
  state.authenticatedUser = null;
  pushLine(state, '', 'normal');
  pushLine(state, 'SESIÓN CERRADA. HASTA LA PRÓXIMA, PILOTO.', 'msg');
  audio.playLoginOk();
  onChange();
}

interface TransitionOptions {
  resetLog?: boolean;
  keepLog?: boolean;
}

async function transitionTo(
  view: ViewId,
  opts: Options,
  trans: TransitionOptions = {},
): Promise<void> {
  const { state, onChange } = opts;

  // Salida de la vista anterior (limpieza de turnstile, etc.).
  await onLeaveView(state.view, opts);

  state.view = view;
  clearInput(state);
  state.inputIsPassword = isPasswordView(view);
  if (trans.resetLog) clearLog(state);

  // Entrada de la nueva vista (textos placeholder, etc.).
  await onEnterView(view, opts);

  onChange();
}

/* ─── Hooks de entrada / salida de vista ──────────────────────────────── */

async function onEnterView(view: ViewId, opts: Options): Promise<void> {
  const { state, onChange } = opts;

  switch (view) {
    case 'verify_email_checking': {
      // Verificación automática: el token vino en la URL. Pintamos el
      // estado "VERIFICANDO..." y consumimos el token contra el backend.
      state.inputDisabled = true;
      onChange();
      const token = state.verifyToken;
      if (!token) {
        pushLine(state, messageFor('invalid_token'), 'error');
        pushLine(state, '', 'normal');
        await transitionTo('verify_email_resend', opts, { keepLog: true });
        return;
      }
      const result = await api.verifyEmail({ token });
      if (!result.ok) {
        pushLine(state, messageFor(result.code, result.error), 'error');
        pushLine(state, '', 'normal');
        await transitionTo('verify_email_resend', opts, { keepLog: true });
        return;
      }
      await transitionTo('verify_email_done', opts, { keepLog: true });
      return;
    }

    case 'verify_email_done':
      pushLine(state, '', 'normal');
      pushLine(state, 'EMAIL VERIFICADO CORRECTAMENTE.', 'msg');
      pushLine(state, 'YA PUEDES INICIAR SESIÓN.', 'normal');
      pushLine(state, '', 'normal');
      pushLine(state, 'REDIRIGIENDO AL LOGIN...', 'info');
      audio.playLoginOk();
      window.setTimeout(() => window.location.replace('/login.html'), 1800);
      break;

    case 'verify_email_resend':
      pushLine(state, '', 'normal');
      pushLine(state, 'REENVIAR VERIFICACIÓN', 'title');
      pushLine(state, '', 'normal');
      pushLine(
        state,
        'INTRODUCE TU EMAIL Y TE REENVIAREMOS EL ENLACE.',
        'info',
      );
      break;

    case 'verify_email_resent':
      pushLine(state, '', 'normal');
      pushLine(state, 'EMAIL ENVIADO.', 'msg');
      pushLine(
        state,
        'REVISA TU BANDEJA DE ENTRADA Y LA CARPETA DE SPAM.',
        'normal',
      );
      audio.playLoginOk();
      break;

    case 'recovery_request':
      pushLine(state, '', 'normal');
      pushLine(state, 'RECUPERAR PASSWORD', 'title');
      pushLine(state, '', 'normal');
      pushLine(
        state,
        'INTRODUCE TU EMAIL PARA RECIBIR INSTRUCCIONES.',
        'info',
      );
      break;

    case 'recovery_sent':
      pushLine(state, '', 'normal');
      pushLine(state, 'SOLICITUD REGISTRADA.', 'msg');
      pushLine(
        state,
        'SI EL EMAIL EXISTE, RECIBIRÁS UN ENLACE PARA',
        'normal',
      );
      pushLine(
        state,
        'RESTABLECER LA PASSWORD. REVISA TAMBIÉN EL SPAM.',
        'normal',
      );
      audio.playLoginOk();
      break;

    case 'recovery_set_password':
      pushLine(state, '', 'normal');
      pushLine(state, 'NUEVA PASSWORD', 'title');
      pushLine(state, '', 'normal');
      pushLine(
        state,
        'ELIGE UNA PASSWORD NUEVA (MÍNIMO 10 CARACTERES).',
        'info',
      );
      break;

    case 'config':
      pushLine(state, '', 'normal');
      pushLine(state, '4. CONFIG', 'title');
      pushLine(state, '', 'normal');
      break;

    case 'config_screen_profile':
      pushLine(state, '', 'normal');
      pushLine(state, 'PERFIL DE PANTALLA', 'title');
      pushLine(state, '', 'normal');
      break;

    case 'about':
      pushLine(state, '', 'normal');
      pushLine(state, '5. ACERCA DE', 'title');
      pushLine(state, '', 'normal');
      break;

    case 'about_doc': {
      // Volcar el documento legal seleccionado, formateado para el CRT.
      if (state.aboutDocId) {
        const doc = getLegalDocument(state.aboutDocId);
        for (const line of formatLegalDocForCrt(doc)) {
          pushLine(state, line.text, line.level);
        }
      }
      break;
    }

    case 'register_done':
      pushLine(state, '', 'normal');
      pushLine(state, 'REGISTRO COMPLETADO.', 'msg');
      pushLine(state, 'TE HEMOS ENVIADO UN EMAIL DE VERIFICACIÓN.', 'normal');
      pushLine(state, 'ÁBRELO PARA ACTIVAR LA CUENTA. REVISA EL SPAM.', 'normal');
      audio.playLoginOk();
      break;

    case 'login_callsign':
      pushLine(state, '', 'normal');
      pushLine(state, '1. LOGIN', 'title');
      break;
    case 'register_callsign':
      pushLine(state, '', 'normal');
      pushLine(state, '2. REGISTRO', 'title');
      break;

    case 'register_turnstile':
      pushLine(state, '', 'normal');
      pushLine(state, 'COMPLETA LA VERIFICACIÓN ANTI-BOT:', 'info');
      onChange();
      await mountTurnstileWidget(opts);
      break;

    case 'menu':
    case 'login_password':
    case 'register_email':
    case 'register_password':
    case 'register_password_confirm':
    case 'login_busy':
    case 'register_busy':
      // Sin acción adicional al entrar.
      break;
  }

  // Vistas tipo "mensaje" (sin selector ni input) → cursor desactivado.
  if (
    view === 'about_doc' ||
    view === 'register_done' ||
    view === 'verify_email_done' ||
    view === 'verify_email_resent' ||
    view === 'recovery_sent'
  ) {
    state.inputDisabled = true;
  }
}

async function onLeaveView(view: ViewId, opts: Options): Promise<void> {
  if (view === 'register_turnstile' || view === 'register_busy') {
    await unmountTurnstileWidget(opts);
  }
}

/* ─── Widget Turnstile como dialog DOS ────────────────────────────────── */

async function mountTurnstileWidget(opts: Options): Promise<void> {
  const { state, refs } = opts;

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  if (!siteKey) {
    pushLine(state, 'CONFIG: VITE_TURNSTILE_SITE_KEY NO DEFINIDA.', 'error');
    return;
  }

  // Inserta el div del widget al final del log "manualmente" — el
  // render lo dibuja como una línea más, pero el widget de Cloudflare
  // necesita un container persistente. Lo añadimos como hermano del
  // <pre.dos> para no perderlo en cada re-render.
  let host = refs.terminal.querySelector<HTMLElement>('#turnstile-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'turnstile-host';
    host.className = 'crt-turnstile';
    refs.terminal.appendChild(host);
  }
  host.style.display = 'block';
  host.innerHTML = '';

  try {
    const turnstile = await loadTurnstile();
    widgetId = turnstile.render(host, {
      sitekey: siteKey,
      theme: 'dark',
      callback: (token) => {
        state.turnstileToken = token;
        // Solo en dev: exponemos el token al window para que los smoke
        // tests de Playwright sepan cuándo se ha resuelto el reto.
        if (import.meta.env.DEV) {
          (window as Window & { __crtTurnstileToken?: string }).__crtTurnstileToken = token;
        }
      },
      'expired-callback': () => {
        state.turnstileToken = null;
      },
      'error-callback': () => {
        state.turnstileToken = null;
      },
    });
  } catch (err) {
    console.error('[auth/turnstile] falló la carga:', err);
    pushLine(state, 'NO SE PUDO CARGAR EL VERIFICADOR ANTI-BOT.', 'error');
  }
}

async function unmountTurnstileWidget(opts: Options): Promise<void> {
  const { refs } = opts;
  if (widgetId !== null && window.turnstile) {
    try {
      window.turnstile.reset(widgetId);
    } catch {
      /* ignore */
    }
  }
  widgetId = null;
  const host = refs.terminal.querySelector<HTMLElement>('#turnstile-host');
  if (host) host.style.display = 'none';
}

/* ─── Utilidades ──────────────────────────────────────────────────────── */

function visiblePrompt(view: ViewId): string {
  // Mismo mapeo que `promptForView` pero para imprimir en el log al
  // confirmar (sin mensajes auxiliares de busy).
  switch (view) {
    case 'login_callsign':
    case 'register_callsign':
      return 'CALLSIGN:';
    case 'login_password':
    case 'register_password':
      return 'PASSWORD:';
    case 'register_email':
    case 'verify_email_resend':
    case 'recovery_request':
      return 'EMAIL:';
    case 'register_password_confirm':
      return 'PASSWORD (CONFIRMAR):';
    case 'recovery_set_password':
      return 'NUEVA PASSWORD:';
    default:
      return '>';
  }
}

/**
 * Cuando hemos cambiado a una vista busy (login_busy / register_busy)
 * para impedir nuevos inputs, processInput debe ver la vista
 * "anterior" para saber qué efecto aplicar. Este helper hace el
 * mapeo inverso.
 */
function previousActionableView(busyView: ViewId): ViewId {
  if (busyView === 'login_busy') return 'login_password';
  if (busyView === 'register_busy') return 'register_turnstile';
  return busyView;
}
